import { z } from "zod";

export const PRINT_TEMPLATE_VERSION = 1 as const;
export const PRINT_TEMPLATE_MAX_SECTIONS = 20;
export const PRINT_TEMPLATE_MAX_ITEMS = 60;
export const PRINT_TEMPLATE_MAX_COLUMNS = 4;

export const PrintInventoryTypeSchema = z.enum([
  "ARBRE",
  "ALIGNEMENT",
  "EPAYSAGE",
]);

/** Couleur CSS en hexadécimal (#rrggbb) : jamais de texte libre, le style est appliqué tel quel au rendu. */
export const PrintColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/** Style d'une section : couleur de la barre de titre, couleur du texte du titre, fond du contenu. Vide = style par défaut. */
export const PrintSectionStyleSchema = z.object({
  titleBg: PrintColorSchema.optional(),
  titleColor: PrintColorSchema.optional(),
  background: PrintColorSchema.optional(),
});

/** Un champ placé dans une section : visibilité, colonne, libellé. L'ordre du tableau est l'ordre d'affichage dans la colonne. */
export const PrintTemplateItemSchema = z.object({
  field: z.string().min(1).max(64),
  visible: z.boolean(),
  col: z.number().int().min(1).max(PRINT_TEMPLATE_MAX_COLUMNS).default(1),
  label: z.string().trim().max(60).optional(),
  hideIfEmpty: z.boolean().optional(),
});

export const PrintTemplateSectionSchema = z.object({
  id: z.string().min(1).max(64),
  /** `builtin` : section connue du registre. `custom` : section créée par l'administrateur (réservé aux versions suivantes). */
  kind: z.enum(["builtin", "custom"]).default("builtin"),
  visible: z.boolean(),
  title: z.string().trim().max(80).optional(),
  columns: z.number().int().min(1).max(PRINT_TEMPLATE_MAX_COLUMNS).default(3),
  style: PrintSectionStyleSchema.optional(),
  items: z.array(PrintTemplateItemSchema).max(PRINT_TEMPLATE_MAX_ITEMS),
});

export const PrintTemplateHeaderSchema = z.object({
  showLogo: z.boolean(),
  showOrganisation: z.boolean(),
  showSiret: z.boolean(),
  showRecenseur: z.boolean(),
  /** Couleur de la barre du titre principal (par défaut : vert de la fiche). */
  titleBg: PrintColorSchema.optional(),
  titleColor: PrintColorSchema.optional(),
});

export const PrintTemplateSchema = z.object({
  version: z.literal(PRINT_TEMPLATE_VERSION),
  inventoryType: PrintInventoryTypeSchema,
  header: PrintTemplateHeaderSchema,
  sections: z
    .array(PrintTemplateSectionSchema)
    .max(PRINT_TEMPLATE_MAX_SECTIONS),
});

export type PrintInventoryType = z.infer<typeof PrintInventoryTypeSchema>;
export type PrintSectionStyle = z.infer<typeof PrintSectionStyleSchema>;
export type PrintTemplateItem = z.infer<typeof PrintTemplateItemSchema>;
export type PrintTemplateSection = z.infer<typeof PrintTemplateSectionSchema>;
export type PrintTemplateHeader = z.infer<typeof PrintTemplateHeaderSchema>;
export type PrintTemplate = z.infer<typeof PrintTemplateSchema>;
